/* eslint-disable react/no-unescaped-entities */
import * as React from 'react';

import {
  createStyles,
  Grid,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@nelnet/unifi-components-react';

import Link from '../components/modules/Link';
import Button from '../components/modules/Button';
import PrimaryLayout from '../layouts/PrimaryLayout';
import UnifiTheme from '../themes/unifi/UnifiTheme';
import PlatformCardGrid from '../components/layout/PlatformCardGrid';

const hero = require('../assets/images/hero.svg');
const studioIllustration = require('../assets/images/studio-illustration.svg');

const styles = ({ spacing, palette }: Theme) =>
  createStyles({
    wrapper: {
      margin: '0 auto',
      '& p': {
        maxWidth: '720px',
      },
    },
    collectiveWrapper: {
      position: 'relative',
      backgroundColor: palette.secondary.dark,
      padding: `0 ${spacing(4)}px`,
      margin: `${spacing(5)}px -${spacing(3)}px 0`,
    },
    collective: {
      color: '#fff',
      padding: `${spacing(6)}px ${spacing(2)}px`,
      '& p a': {
        color: 'inherit',
        textDecoration: 'underline',
      },
      '& p a:hover': {
        textDecoration: 'none',
      },
      '& p a:focus': {
        outline: 'none',
        border: '3px solid transparent',
        boxShadow: '0 0 6px 2px currentColor',
        borderRadius: '2px',
        margin: '-3px',
      },
    },
    collectiveHighlight: {
      textAlign: 'center',
      '& img': {
        height: '20vh',
        display: 'block',
      },
      '& a[class*="Button"]': {
        alignSelf: 'center',
      },
    },
    collectiveJoinIcon: {
      width: '24vh',
      height: '20vh',
      border: '2px dashed #fff',
      borderRadius: '8px',
      margin: '0 auto',
      display: 'grid',
      placeItems: 'center',
      gridTemplateAreas: '"icon"',
      '&::before, &::after': {
        content: '""',
        width: '10px',
        height: '40px',
        backgroundColor: '#fff',
        borderRadius: '2px',
        gridArea: 'icon',
      },
      '&:after': {
        transform: 'rotate(90deg)',
      },
    },
  });

const IndexPage: React.FunctionComponent<WithStyles<typeof styles>> = ({ classes }) => {
  return (
    <PrimaryLayout title="Home" path="">
      <PlatformCardGrid link="components" />
      <div id="collective" className={classes.collectiveWrapper}>
        <Grid
          container
          constrained
          spacing={3}
          component="section"
          margin={{ x: 'auto', top: 4 }}
          className={classes.collective}
        >
          <Typography variant="h2" gutterBottom>
            Unifi for Great Customer Experience (CX)
          </Typography>
          <Typography variant="body1">
            Unifi is a collective of knowledge, people, tools, technologies, processes and
            capabilities focused on empowering Nelnet to deliver exceptional customer experiences.{' '}
          </Typography>

          <UnifiTheme>
            <Grid
              container
              spacing={6}
              justify="center"
              alignItems="flex-start"
              margin={{ top: 4 }}
            >
              <Grid
                item
                md={4}
                container
                direction="column"
                justify="center"
                className={classes.collectiveHighlight}
              >
                <img src={hero} role="presentation" alt="" />
                <Typography variant="h3" gutterBottom margin={{ top: 2 }}>
                  Leverage the <strong>Unifi&nbsp;Design&nbsp;System</strong>
                </Typography>
                <Typography>
                  Unifi began as a grassroots effort to create the <strong>Design System</strong>.
                  It provides design patterns and multi-platform components to efficiently build
                  digital user interfaces in a consistent and accessible way.
                </Typography>
              </Grid>
              <Grid
                item
                md={4}
                container
                direction="column"
                justify="center"
                className={classes.collectiveHighlight}
              >
                <img src={studioIllustration} role="presentation" alt="" />
                <Typography variant="h3" gutterBottom margin={{ top: 2 }}>
                  Partner with the <strong>Unifi&nbsp;Design&nbsp;Studio</strong>
                </Typography>
                <Typography>
                  The former UX/UI team evolved to become the{' '}
                  <Link href={'https://studio.unifi.nelnet.io/'} target="_blank" rel="noopener">
                    Unifi Design Studio
                  </Link>
                  . A team dedicated to informing, supporting, and visualizing exceptional customer
                  experiences.
                </Typography>
              </Grid>
              <Grid
                item
                md={4}
                container
                direction="column"
                justify="center"
                className={classes.collectiveHighlight}
              >
                <div className={classes.collectiveJoinIcon}></div>
                <Typography variant="h3" gutterBottom margin={{ top: 2 }}>
                  Join the <br />
                  <strong>Unifi&nbsp;Movement</strong>
                </Typography>
                <Typography>
                  If you'd like to become a part of Unifi, or leverage Unifi for your project, we'd
                  love to hear from you.
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  to="mailto:getcx@nelnet.net"
                  margin={{ top: 2 }}
                >
                  Reach Out
                </Button>
              </Grid>
            </Grid>
          </UnifiTheme>
        </Grid>
      </div>
      <Grid
        container
        justify="center"
        constrained
        spacing={3}
        component="section"
        className={classes.wrapper}
      >
        <Grid
          item
          xs="auto"
          container
          direction="column"
          alignItems="center"
          className={classes.collectiveHighlight}
          margin={{ y: 4 }}
        >
          <Typography
            component="p"
            variant="h3"
            color="primary"
            align="center"
            margin={{ bottom: 3 }}
          >
            Wondering what a design system is, or if it's available for your platform? Read the
            frequently asked questions.
          </Typography>
          <Button variant="outlined" to="/faqs">
            FAQs
          </Button>
        </Grid>
      </Grid>
    </PrimaryLayout>
  );
};

export default withStyles(styles)(IndexPage);
